import React, { useState } from "react";

// Shared styles (match App.tsx)
const eyebrow: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.14em",
  textTransform: "uppercase", margin: "0 0 14px", display: "block",
};
const sectionHeading: React.CSSProperties = {
  fontSize: "clamp(1.9rem, 4vw, 2.6rem)", fontWeight: 800, color: "#0f172a",
  letterSpacing: "-0.025em", lineHeight: 1.15, margin: "0 0 24px",
};
const sectionIntro: React.CSSProperties = {
  fontSize: 17, lineHeight: 1.8, color: "#64748b", margin: "0 0 56px", maxWidth: 640,
};

// -- HOW IT WORKS -
const STEPS: [string, string][] = [
  ["Confidential intro", "Reach out through the form, email, or a short call. Nothing is shared with anyone, and there's no obligation."],
  ["Get to know each other", "We talk about your business, your people, your timing, and what you want for yourself after a transition. You get to size me up too."],
  ["Careful diligence", "If it feels like a fit, we look at the numbers and operations together under an NDA, at a pace that doesn't disrupt your business."],
  ["A handoff that holds", "We structure a transition that protects your customers, your employees, and the reputation you built."],
];

export function HowItWorks() {
  return (
    <section id="process" style={{ padding: "120px 2rem", background: "#fafbfc" }}>
      <div style={{ maxWidth: 1040, margin: "0 auto" }}>
        <span style={eyebrow}>How it works</span>
        <h2 style={sectionHeading}>What a conversation with me looks like</h2>
        <p style={sectionIntro}>No pressure and no sales process. We move at your pace, one conversation at a time.</p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "40px 48px" }}>
          {STEPS.map(([title, desc], i) => (
            <div key={title}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#2d5a8e", letterSpacing: "0.08em", marginBottom: 12 }}>{String(i + 1).padStart(2, "0")}</div>
              <h4 style={{ fontSize: 17, fontWeight: 700, margin: "0 0 10px", color: "#0f172a" }}>{title}</h4>
              <p style={{ color: "#64748b", fontSize: 15, lineHeight: 1.8, margin: 0 }}>{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -- FAQ -
const FAQS: [string, string][] = [
  ["How did you get my name, and why did I get a letter?",
   "I build my list from public business directories, filtered for industries I understand and companies that have been around a long time. I write to owners directly because I'd rather get to know you than go through a sale process. If you'd prefer not to hear from me again, just let me know and I'll take you off my list."],
  ["We're not for sale. Why would I talk to you?",
   "Many of the best transitions start years before anything is listed. A confidential conversation costs you nothing and helps clarify your options while you still have plenty of room to choose."],
  ["Are you private equity? Who's behind you?",
   "No. I'm an individual buyer. I plan to run one business myself, full time, for the long term. I'm not flipping it or rolling it up into a bigger company. Financing would come from an SBA-backed bank loan plus my own money, not an investment fund."],
  ["What size business are you looking for?",
   "Roughly $500K to $1.5M a year in owner earnings. That's your profit plus what you pay yourself and any personal expenses run through the business (sometimes called SDE, or seller's discretionary earnings). Revenue matters less than that number. If you're not sure where you fall, that's a fine place to start a conversation."],
  ["Will my employees or customers find out?",
   "Not from me. Early conversations stay private, and I'll sign an NDA before looking at anything sensitive. Any wider communication would be planned with you, on your timeline."],
  ["What happens to my employees?",
   "The team is usually the most valuable part of a business, and I'm not buying one to cut it. My plan is to keep your people, learn from them, and invest in growth."],
  ["How would I get paid?",
   "In most small business sales, the majority is paid in cash at closing, funded by an SBA-backed bank loan and the buyer's own money. It's also common for the owner to carry a smaller portion as a note that's paid over time. We'd work out a structure that fits your goals, and I'd encourage you to involve your own CPA and attorney."],
  ["How do you decide what my business is worth?",
   "Small businesses are usually valued as a multiple of owner earnings, adjusted for things like how dependent the business is on you, how concentrated the customer base is, the condition of equipment, and growth. I'll walk you through my reasoning openly. No lowball opening offer."],
  ["How long does it take, and what would you need from me?",
   "A typical timeline is three to six months from a signed letter of intent to closing. Early on I only need a few years of tax returns and financial statements. After closing, most owners stay on for a defined transition period, often a few months, to introduce customers and hand off know-how."],
  ["Do I need a broker?",
   "That's entirely your call. Some owners prefer one; others would rather work directly with a buyer and lean on their CPA and attorney. I'm happy to work either way."],
  ["What happens to my company's name and reputation?",
   "I'd keep the name. Your customers, suppliers, and employees chose your company for a reason, and continuity protects everyone."],
  ["Where are you based? Would you move?",
   "I live in Santa Clara County and I'm looking across Northern California and the West Coast. For the right business, I'm willing to relocate and become part of the community."],
  ["I'm several years from retiring. Is it too early to talk?",
   "Not at all. Some owners want to plan years ahead; others want to step back gradually. There's no obligation and no pressure."],
];

export function FAQ() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" style={{ padding: "120px 2rem", background: "white" }}>
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <span style={eyebrow}>Questions owners ask</span>
        <h2 style={sectionHeading}>Frequently asked questions</h2>
        <p style={sectionIntro}>If your question isn't here, ask me directly. I'm glad to answer anything.</p>
        <div style={{ borderTop: "1px solid #e2e8f0" }}>
          {FAQS.map(([q, a], i) => {
            const isOpen = open === i;
            return (
              <div key={q} style={{ borderBottom: "1px solid #e2e8f0" }}>
                <button
                  onClick={() => setOpen(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, background: "none", border: "none", cursor: "pointer", padding: "22px 0", textAlign: "left", fontSize: 17, fontWeight: 600, color: "#0f172a", fontFamily: "inherit" }}
                >
                  <span>{q}</span>
                  <span style={{ fontSize: 22, color: "#94a3b8", flexShrink: 0, lineHeight: 1 }}>{isOpen ? "−" : "+"}</span>
                </button>
                {isOpen && <p style={{ margin: "0 0 24px", color: "#475569", fontSize: 16, lineHeight: 1.8, paddingRight: 32 }}>{a}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// -- CONTACT FORM (Netlify Forms) -
// The hidden static form in public/index.html must list every field name below,
// otherwise Netlify drops the field from submissions.
type Role = "Owner" | "Broker" | "Advisor" | "Other";

const ROLE_COPY: Record<Role, string> = {
  Owner: "Business owner",
  Broker: "Business broker",
  Advisor: "CPA, attorney, or advisor",
  Other: "Something else",
};

const label: React.CSSProperties = { display: "block", fontSize: 13, fontWeight: 600, color: "#334155", margin: "0 0 6px" };
const input: React.CSSProperties = { width: "100%", boxSizing: "border-box", padding: "11px 12px", border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 15, fontFamily: "inherit", color: "#0f172a", background: "white" };
const fieldWrap: React.CSSProperties = { marginBottom: 18 };

function Chips({ options, value, onChange, multi = false }: { options: string[]; value: string | string[]; onChange: (v: any) => void; multi?: boolean }) {
  const selected = (o: string) => (multi ? (value as string[]).includes(o) : value === o);
  const toggle = (o: string) => {
    if (!multi) return onChange(value === o ? "" : o);
    const arr = value as string[];
    onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
  };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {options.map((o) => (
        <button type="button" key={o} onClick={() => toggle(o)}
          style={{ padding: "8px 14px", borderRadius: 20, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
            border: selected(o) ? "1px solid #1e3a5f" : "1px solid #cbd5e1",
            background: selected(o) ? "#1e3a5f" : "white", color: selected(o) ? "white" : "#334155" }}>
          {o}
        </button>
      ))}
    </div>
  );
}

function Field({ name, title, value, onChange, placeholder, type = "text", textarea = false }: { name: string; title: string; value: string; onChange: (n: string, v: string) => void; placeholder?: string; type?: string; textarea?: boolean }) {
  return (
    <div style={fieldWrap}>
      <label htmlFor={`f-${name}`} style={label}>{title}</label>
      {textarea ? (
        <textarea id={`f-${name}`} name={name} rows={4} value={value} placeholder={placeholder} onChange={(e) => onChange(name, e.target.value)} style={{ ...input, resize: "vertical" }} />
      ) : (
        <input id={`f-${name}`} name={name} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(name, e.target.value)} style={input} />
      )}
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (<div style={fieldWrap}><span style={label}>{title}</span>{children}</div>);
}

export function ContactForm() {
  const [role, setRole] = useState<Role | "">("");
  const [f, setF] = useState<Record<string, string>>({});
  const [priorities, setPriorities] = useState<string[]>([]);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const set = (n: string, v: string) => setF((p) => ({ ...p, [n]: v }));
  // Switching roles clears answers that belong to the previous role; keeps contact details.
  const KEEP = ["name", "company", "email", "phone", "message"];
  const changeRole = (r: Role) => {
    if (r === role) return;
    setRole(r);
    setPriorities([]);
    setF((p) => Object.fromEntries(Object.entries(p).filter(([k]) => KEEP.includes(k))));
  };
  const v = (n: string) => f[n] || "";
  const chip = (n: string) => (val: string) => set(n, val);

  const canSubmit = role && v("name").trim() && (v("email").trim() || v("phone").trim());

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("sending");
    const data: Record<string, string> = { "form-name": "contact", role, ...f, priorities: priorities.join(", ") };
    const body = Object.keys(data).map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k] || "")).join("&");
    try {
      const res = await fetch("/", { method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body });
      if (!res.ok) throw new Error(String(res.status));
      setStatus("sent");
      const gtag = (window as any).gtag;
      if (gtag) gtag("event", "generate_lead", { role });
    } catch {
      setStatus("error");
    }
  };

  const card: React.CSSProperties = { background: "white", color: "#0f172a", borderRadius: 14, padding: "28px 24px", textAlign: "left", boxShadow: "0 20px 50px rgba(0,0,0,0.25)" };

  if (status === "sent") {
    return (
      <div style={card}>
        <h3 style={{ margin: "0 0 10px", fontSize: 20 }}>Thank you. Your message is on its way.</h3>
        <p style={{ margin: 0, color: "#475569", lineHeight: 1.7 }}>
          I'll reply personally within one business day. Everything you shared stays confidential.
          {role === "Broker" && <> In the meantime, my <a href="/Matsubu-BuyerProfile-2026.pdf" download style={{ color: "#1d4ed8" }}>buyer profile</a> has my background and criteria, and I can turn around an NDA within 24 hours.</>}
        </p>
      </div>
    );
  }

  return (
    <form name="contact" method="POST" data-netlify="true" netlify-honeypot="bot-field" onSubmit={submit} style={card}>
      <input type="hidden" name="form-name" value="contact" />
      <p style={{ display: "none" }}><label>Leave this empty: <input name="bot-field" onChange={(e) => set("bot-field", e.target.value)} /></label></p>

      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 18 }}>Confidential · About a minute · I reply within one business day</div>

      <Group title="Who are you?">
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {(Object.keys(ROLE_COPY) as Role[]).map((r) => (
            <button type="button" key={r} onClick={() => changeRole(r)}
              style={{ padding: "10px 16px", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                border: role === r ? "2px solid #1e3a5f" : "1px solid #cbd5e1", background: role === r ? "#eff6ff" : "white", color: "#0f172a" }}>
              {ROLE_COPY[r]}
            </button>
          ))}
        </div>
      </Group>

      {role === "Owner" && (<>
        <Group title="Where are you in your thinking?">
          <Chips options={["Just curious", "Thinking about the next 1 to 3 years", "Ready to talk now", "Already working with a broker"]} value={v("stage")} onChange={chip("stage")} />
          {v("stage") === "Already working with a broker" && <p style={{ fontSize: 13, color: "#475569", margin: "10px 0 0" }}>That's great. Feel free to include your broker's contact below and I'll go through them.</p>}
        </Group>
        <Group title="Type of business (optional)">
          <Chips options={["Manufacturing", "Lab or testing", "Industrial or field services", "Environmental", "Other"]} value={v("industry")} onChange={chip("industry")} />
        </Group>
        <Field name="city" title="City (optional)" value={v("city")} onChange={set} placeholder="e.g. Fremont, Eureka" />
        <Group title="Team size (optional)">
          <Chips options={["1 to 10", "11 to 25", "26 to 50", "50+"]} value={v("team_size")} onChange={chip("team_size")} />
        </Group>
        <Group title="What matters most to you in a transition? (optional, pick any)">
          <Chips multi options={["My employees", "My customers", "Price", "Timing", "Staying involved", "Company name and legacy"]} value={priorities} onChange={setPriorities} />
        </Group>
      </>)}

      {role === "Broker" && (<>
        <Group title="What do you have?">
          <Chips options={["Listing I can share now", "Pre-market / coming soon", "Deal that lost its buyer", "Add me to your buyer list"]} value={v("stage")} onChange={chip("stage")} />
        </Group>
        <Field name="listing" title="Listing name or link (optional)" value={v("listing")} onChange={set} />
        <Field name="industry" title="Industry and location (optional)" value={v("industry")} onChange={set} />
        <Group title="SDE range (optional)">
          <Chips options={["Under $500K", "$500K to $1M", "$1M to $1.5M", "$1.5M+"]} value={v("sde_range")} onChange={chip("sde_range")} />
        </Group>
        <Field name="asking_price" title="Asking price (optional)" value={v("asking_price")} onChange={set} />
      </>)}

      {role === "Advisor" && (<>
        <Group title="What's the situation?">
          <Chips options={["Client considering succession", "Client wants a sounding board on value", "Introducing myself", "Other"]} value={v("stage")} onChange={chip("stage")} />
        </Group>
        <Field name="industry" title="Client's industry and location (optional)" value={v("industry")} onChange={set} />
        <Group title="Client's timing (optional)">
          <Chips options={["Now", "1 to 3 years", "Later"]} value={v("client_timing")} onChange={chip("client_timing")} />
        </Group>
        <Group title="How would you like to be involved?">
          <Chips options={["Make an introduction", "Stay in the loop", "Keep my client anonymous for now"]} value={v("involvement")} onChange={chip("involvement")} />
        </Group>
      </>)}

      {role && (<>
        <div style={{ borderTop: "1px solid #e2e8f0", margin: "8px 0 20px" }} />
        <Field name="name" title="Your name" value={v("name")} onChange={set} />
        {role !== "Owner" && <Field name="company" title={role === "Broker" ? "Brokerage" : "Firm or company"} value={v("company")} onChange={set} />}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 16px" }}>
          <Field name="email" title="Email" type="email" value={v("email")} onChange={set} placeholder="Email or phone is fine" />
          <Field name="phone" title="Phone" type="tel" value={v("phone")} onChange={set} placeholder="Email or phone is fine" />
        </div>
        {role === "Owner" && (<>
          <Field name="best_time" title="Best time to reach you (optional)" value={v("best_time")} onChange={set} placeholder="e.g. weekday mornings" />
          <Group title="How did you hear about me? (optional)">
            <Chips options={["Letter", "Email", "Referral", "Search or other"]} value={v("source")} onChange={chip("source")} />
          </Group>
          {v("source") === "Letter" && <Field name="letter_code" title="Code from the letter, if there is one (optional)" value={v("letter_code")} onChange={set} />}
        </>)}
        <Field name="message" title={role === "Other" ? "What brings you here?" : "Anything else? (optional)"} value={v("message")} onChange={set} textarea placeholder="Timing, people, questions, anything useful" />

        <button type="submit" disabled={!canSubmit || status === "sending"}
          style={{ width: "100%", background: canSubmit ? "#1e3a5f" : "#94a3b8", color: "white", border: "none", borderRadius: 9, padding: "14px 20px", fontWeight: 700, fontSize: 15, cursor: canSubmit ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
          {status === "sending" ? "Sending..." : "Send confidentially"}
        </button>
        {status === "error" && <p style={{ color: "#b91c1c", fontSize: 14, margin: "12px 0 0" }}>Something went wrong. Please email me directly at john@jmatsubu.com.</p>}
        {!canSubmit && <p style={{ color: "#64748b", fontSize: 12, margin: "10px 0 0" }}>Name plus an email or phone number is all I need.</p>}
      </>)}
    </form>
  );
}
